import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { ChecklistItem } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

type Stage = ChecklistItem['stage']

const stageOptions: { value: Stage; label: string }[] = [
  { value: 'start', label: 'Before start' },
  { value: 'complete', label: 'Before complete' },
]

/**
 * What each section must tick before it starts a job or before it passes one
 * on. One list per section, each item asked at one of the two moments; rows
 * show the start items first, and order within each group is the order staff
 * see. Inactive items are hidden from the dialogs, and history keeps the
 * wording used at the time.
 */
export default function AdminChecklist() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [departmentId, setDepartmentId] = useState<number | null>(null)
  const [text, setText] = useState('')
  const [stage, setStage] = useState<Stage>('complete')
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [deleting, setDeleting] = useState<ChecklistItem | null>(null)

  const departments = useAdminDepartments()

  const items = useQuery({
    queryKey: ['admin-checklist-items'],
    queryFn: async () => (await api.get<{ data: ChecklistItem[] }>('/admin/checklist-items')).data.data,
  })

  const refresh = (res: { message: string }) => {
    queryClient.invalidateQueries({ queryKey: ['admin-checklist-items'] })
    queryClient.invalidateQueries({ queryKey: ['staff-checklist-items'] })
    toast.success(res.message)
  }
  const fail = (title: string) => (err: unknown) => toast.error(title, errorMessage(err))

  const add = useMutation({
    mutationFn: async (body: { department_id: number; text: string; stage: Stage }) =>
      (await api.post<{ message: string }>('/admin/checklist-items', body)).data,
    onSuccess: (res) => {
      refresh(res)
      setText('')
    },
    onError: fail('Could not add item'),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: number; text?: string; active?: boolean; stage?: Stage }) =>
      (await api.patch<{ message: string }>(`/admin/checklist-items/${id}`, body)).data,
    onSuccess: (res, vars) => {
      refresh(res)
      if (vars.text !== undefined) {
        setEdits((prev) => {
          const next = { ...prev }
          delete next[vars.id]
          return next
        })
      }
    },
    onError: fail('Save failed'),
  })

  const reorder = useMutation({
    mutationFn: async (ids: number[]) => (await api.put<{ message: string }>('/admin/checklist-items/reorder', { ids })).data,
    onSuccess: refresh,
    onError: fail('Reorder failed'),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete<{ message: string }>(`/admin/checklist-items/${id}`)).data,
    onSuccess: (res) => {
      refresh(res)
      setDeleting(null)
    },
    onError: (err) => {
      setDeleting(null)
      fail('Could not delete')(err)
    },
  })

  if (departments.isLoading || items.isLoading) return <LogoLoader />

  const sections = departments.data?.departments ?? []
  const all = items.data ?? []
  const selectedId = departmentId ?? sections[0]?.id ?? null
  const selected = sections.find((d) => d.id === selectedId)
  const sectionItems = all.filter((item) => item.department_id === selectedId)
  const startItems = sectionItems.filter((item) => item.stage === 'start')
  const completeItems = sectionItems.filter((item) => item.stage !== 'start')
  // Start items first, the order a job meets them.
  const rows = [...startItems, ...completeItems]

  // Arrows move an item within its own group; the section's whole display
  // order is sent, so the stored order matches what is on screen.
  const move = (item: ChecklistItem, direction: -1 | 1) => {
    const group = (item.stage === 'start' ? startItems : completeItems).map((row) => row.id)
    const index = group.indexOf(item.id)
    const target = index + direction
    if (target < 0 || target >= group.length) return
    ;[group[index], group[target]] = [group[target]!, group[index]!]
    const ids = item.stage === 'start' ? [...group, ...completeItems.map((row) => row.id)] : [...startItems.map((row) => row.id), ...group]
    reorder.mutate(ids)
  }

  return (
    <div>
      <PageHeader title="Checklist" subtitle="What each section must confirm before it starts a job or passes one on" />

      <Card className="mb-5">
        <SectionLabel>Section</SectionLabel>
        <div className="flex flex-wrap gap-1.5">
          {sections.map((dept) => {
            const count = all.filter((item) => item.department_id === dept.id && item.active).length
            const on = dept.id === selectedId
            return (
              <button
                key={dept.id}
                type="button"
                onClick={() => setDepartmentId(dept.id)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-semibold',
                  on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:text-foreground',
                )}
              >
                {dept.sequence}. {dept.name}
                <span className={cn('ml-1.5 text-xs font-normal', on ? 'text-primary/80' : 'text-muted')}>{count}</span>
              </button>
            )
          })}
        </div>
      </Card>

      <Card className="mb-5">
        <SectionLabel>Add an item for {selected?.name ?? 'this section'}</SectionLabel>
        <form
          className="grid gap-2 sm:flex"
          onSubmit={(e) => {
            e.preventDefault()
            if (text.trim() && selectedId) add.mutate({ department_id: selectedId, text: text.trim(), stage })
          }}
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={150}
            placeholder="e.g. Full resolution image"
            className="sm:flex-1"
          />
          <Select value={stage} onChange={(value) => setStage(value as Stage)} options={stageOptions} className="w-full sm:w-48" />
          <Button type="submit" loading={add.isPending} disabled={!text.trim() || !selectedId}>
            <Plus className="size-4" /> Add item
          </Button>
        </form>
      </Card>

      <Table columns={['#', 'Item', 'When', 'Status', 'Order', actionsColumn]}>
        {rows.length === 0 && (
          <EmptyRow colSpan={6}>No items for {selected?.name ?? 'this section'} - staff start and complete with a plain confirmation.</EmptyRow>
        )}
        {rows.map((item) => {
          const group = item.stage === 'start' ? startItems : completeItems
          const index = group.indexOf(item)
          const draft = edits[item.id]
          const dirty = draft !== undefined && draft.trim() !== item.text && draft.trim() !== ''
          const busy = update.isPending && update.variables?.id === item.id

          return (
            <Row key={item.id} className={cn(!item.active && 'opacity-60')}>
              <Cell className="w-10 text-muted">{index + 1}</Cell>
              <Cell>
                <div className="flex min-w-56 items-center gap-1.5">
                  <Input
                    value={draft ?? item.text}
                    maxLength={150}
                    onChange={(e) => setEdits({ ...edits, [item.id]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && dirty) update.mutate({ id: item.id, text: draft.trim() })
                      if (e.key === 'Escape') setEdits({ ...edits, [item.id]: item.text })
                    }}
                  />
                  {dirty && (
                    <Button variant="ghost" className="px-2" loading={busy} onClick={() => update.mutate({ id: item.id, text: draft.trim() })}>
                      Save
                    </Button>
                  )}
                </div>
              </Cell>
              <Cell>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => update.mutate({ id: item.id, stage: item.stage === 'start' ? 'complete' : 'start' })}
                  className={cn(
                    'whitespace-nowrap rounded-lg border px-2.5 py-1 text-xs font-semibold',
                    item.stage === 'start'
                      ? 'border-primary/60 bg-primary/20 text-primary'
                      : 'border-foreground/30 bg-foreground/10 text-foreground/80',
                  )}
                  title={item.stage === 'start' ? 'Click to ask it before completing instead' : 'Click to ask it before starting instead'}
                >
                  {item.stage === 'start' ? 'Before start' : 'Before complete'}
                </button>
              </Cell>
              <Cell>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => update.mutate({ id: item.id, active: !item.active })}
                  className={cn(
                    'rounded-lg border px-2.5 py-1 text-xs font-semibold',
                    item.active ? 'border-success/60 bg-success/20 text-success' : 'border-foreground/30 bg-foreground/10 text-foreground/80',
                  )}
                  title={item.active ? 'Click to stop asking it' : 'Click to require it again'}
                >
                  {item.active ? 'Active' : 'Inactive'}
                </button>
              </Cell>
              <Cell>
                <div className="flex items-center">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(item, -1)}
                    className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={index === group.length - 1 || reorder.isPending}
                    onClick={() => move(item, 1)}
                    className="-m-2 ml-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="size-4" />
                  </button>
                </div>
              </Cell>
              <Cell className={actionsCellClass}>
                <button
                  type="button"
                  aria-label={`Delete ${item.text}`}
                  onClick={() => setDeleting(item)}
                  className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-danger/10 hover:text-status-danger"
                >
                  <Trash2 className="size-4" />
                </button>
              </Cell>
            </Row>
          )
        })}
      </Table>

      <Card className="mt-5 border-primary/20 bg-primary/5">
        <p className="flex items-start gap-2 text-sm text-foreground/80">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>
            "Before start" items are asked when staff press Start work, and a job at that section cannot be completed until it
            has been started there. "Before complete" items are asked when staff press Complete, before the job moves on. Staff
            tick every active item for their section, in this order; inactive items are not asked. The job history keeps the
            wording that was ticked at the time, so renaming or deleting an item later never changes past records. A section
            with no items starts and completes with a plain confirmation.
          </span>
        </p>
      </Card>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title={deleting ? `Delete "${deleting.text}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        loading={remove.isPending}
      >
        Staff at {selected?.name ?? 'this section'} will no longer be asked to confirm it. Jobs already started or completed keep
        it in their history.
      </ConfirmDialog>
    </div>
  )
}
